import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Bell, CheckCheck, CheckCircle2, MailOpen, EyeOff, AlertTriangle } from 'lucide-react'
import { notificationService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'

const priorityStyles = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
}

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true)
  const [notifications, setNotifications] = useState([])
  const [stats, setStats] = useState({ total: 0, unread: 0, high_priority_unread: 0 })
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)

  const loadData = async () => {
    try {
      setLoading(true)
      const [notificationData, notificationStats] = await Promise.all([
        notificationService.getNotifications({ unread_only: showUnreadOnly, include_dismissed: false }),
        notificationService.getStats(),
      ])

      setNotifications(notificationData.notifications || [])
      setStats(notificationStats || { total: 0, unread: 0, high_priority_unread: 0 })
    } catch (error) {
      toast.error(error.message || 'Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [showUnreadOnly])

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markRead(id)
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to mark as read')
    }
  }

  const handleMarkUnread = async (id) => {
    try {
      await notificationService.markUnread(id)
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to mark as unread')
    }
  }

  const handleDismiss = async (id) => {
    try {
      await notificationService.dismiss(id)
      toast.success('Notification dismissed')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to dismiss notification')
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead()
      toast.success('All notifications marked as read')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to mark all as read')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading notifications..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          <p className="text-gray-600 dark:text-gray-400">Stay on top of reminders, dues, and key events.</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowUnreadOnly((prev) => !prev)}
            className="btn-secondary"
          >
            {showUnreadOnly ? 'Show All' : 'Unread Only'}
          </button>
          <button
            onClick={handleMarkAllRead}
            className="btn-primary inline-flex items-center gap-2"
          >
            <CheckCheck className="h-4 w-4" />
            Mark All Read
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Unread</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.unread}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">High Priority Unread</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.high_priority_unread}</p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 inline-flex items-center gap-2">
          <Bell className="h-5 w-5" /> Inbox
        </h2>

        {notifications.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No notifications right now. You're all caught up 🎉</p>
        ) : (
          <div className="space-y-3">
            {notifications.map((item) => {
              const unread = !item.is_read
              const priority = item.priority || 'normal'
              const style = priorityStyles[priority] || priorityStyles.normal

              return (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border ${unread
                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/10'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 dark:text-white">{item.title}</p>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${style}`}>{priority}</span>
                        {unread && (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">Unread</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{item.message}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(item.created_at).toLocaleString()} • {item.type}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {unread ? (
                        <button
                          onClick={() => handleMarkRead(item.id)}
                          className="text-emerald-600 hover:text-emerald-700"
                          title="Mark as read"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMarkUnread(item.id)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Mark as unread"
                        >
                          <MailOpen className="h-4 w-4" />
                        </button>
                      )}

                      <button
                        onClick={() => handleDismiss(item.id)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                        title="Dismiss"
                      >
                        <EyeOff className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {item.priority === 'critical' && (
                    <p className="text-xs mt-2 text-red-600 dark:text-red-300 inline-flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Immediate attention recommended
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
